import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { getProductBySlug, getProducts } from '@/lib/api';
import { useCart } from '@/context/CartContext';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import { Minus, Plus, ShoppingBag, Check, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import { getImageUrl } from '@/components/ImageUploadField';

// Optimise les URLs : resize Unsplash via params, resize MongoDB via ?w=
const optimizeImg = (url, width = 800) => {
  if (!url) return url;
  if (url.startsWith('/api/')) {
    const base = `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return `${base}?w=${width}`;
  }
  if (url.includes('images.unsplash.com')) {
    const base = url.replace(/&w=\d+/, '').replace(/&q=\d+/, '');
    return `${base}&w=${width}&q=80`;
  }
  return url;
};

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await getProductBySlug(slug);
        if (cancelled) return;
        setProduct(response.data);

        // Related products : requête isolée — si elle échoue, la fiche reste
        // affichable au lieu de bloquer sur le catch global.
        if (response.data?.category_id) {
          try {
            const relatedRes = await getProducts({ category_id: response.data.category_id });
            if (!cancelled) {
              setRelatedProducts(
                (relatedRes.data || []).filter(p => p.id !== response.data.id).slice(0, 4)
              );
            }
          } catch (e) {
            // Non bloquant — on affiche juste la fiche sans "Complétez votre intérieur".
            console.warn('Related products failed:', e);
          }
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching product:', error);
        toast.error('Produit non trouvé');
        navigate('/commander-en-ligne');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchProduct();
    setQuantity(1);
    setSelectedImage(0);
    setAdded(false);
    return () => { cancelled = true; };
    // navigate est retiré des deps : useNavigate retourne une nouvelle référence
    // à chaque render dans certaines conditions (concurrent mode, StrictMode double
    // invoke), déclenchant un re-fetch en boucle qui laissait `loading` bloqué.
  }, [slug]);

  const handleAddToCart = () => {
    if (product) {
      addItem(product, quantity);
      setAdded(true);
      toast.success(`${product.name} ajouté au panier`);
      setTimeout(() => setAdded(false), 2000);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="py-12 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              <div className="aspect-square bg-muted rounded-md animate-pulse" />
              <div className="space-y-4">
                <div className="h-8 bg-muted rounded w-3/4" />
                <div className="h-6 bg-muted rounded w-1/4" />
                <div className="h-24 bg-muted rounded" />
              </div>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (!product) {
    return null;
  }

  const images = product.images?.length > 0 
    ? product.images 
    : ['https://images.unsplash.com/photo-1726208224138-80303abb73d3?crop=entropy&cs=srgb&fm=jpg&q=85'];

  return (
    <PublicLayout>
      <SEO
        title={`${product.name} — ${product.category_id ? '' : ''}Tapis & Décoration`}
        description={product.description ? product.description.slice(0, 155) : `Découvrez ${product.name} chez Villa Marceau, concept store bohème à Tours.`}
        canonical={`/produit/${product.slug || ''}`}
        image={product.images?.[0] || undefined}
      />
      <div className="py-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Back button */}
          <Link 
            to="/commander-en-ligne" 
            className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors"
            data-testid="back-to-catalogue"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à la boutique
          </Link>

          <div className="grid lg:grid-cols-[60%_40%] gap-12">
            {/* Images */}
            <div className="space-y-4">
              <motion.div 
                key={selectedImage}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="aspect-square overflow-hidden rounded-md bg-muted"
              >
                <img
                  src={optimizeImg(images[selectedImage])}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  loading="eager"
                  decoding="async"
                />
              </motion.div>
              
              {images.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-colors ${
                        selectedImage === idx ? 'border-primary' : 'border-transparent'
                      }`}
                      data-testid={`thumbnail-${idx}`}
                    >
                      <img src={optimizeImg(img, 160)} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info - Sticky */}
            <div className="lg:sticky lg:top-28 lg:self-start">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <h1 className="font-heading text-3xl md:text-4xl font-semibold mb-2" data-testid="product-name">
                  {product.name}
                </h1>
                <p className="text-2xl text-primary font-semibold mb-6" data-testid="product-price">
                  {typeof product.price === 'number' ? product.price.toFixed(2) : '—'} €
                </p>

                <p className="text-muted-foreground leading-relaxed mb-8" data-testid="product-description">
                  {product.description}
                </p>

                {/* Quantity & Add to cart */}
                <div className="flex items-center gap-4 mb-8">
                  <div className="flex items-center border border-border rounded-full">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="p-3 hover:bg-muted rounded-l-full transition-colors"
                      data-testid="quantity-minus"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-12 text-center font-medium" data-testid="quantity-value">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="p-3 hover:bg-muted rounded-r-full transition-colors"
                      data-testid="quantity-plus"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  <Button
                    onClick={handleAddToCart}
                    className={`btn-primary flex-1 ${added ? 'bg-green-600 hover:bg-green-600' : ''}`}
                    disabled={product.stock === 0}
                    data-testid="add-to-cart-btn"
                  >
                    {added ? (
                      <>
                        <Check className="w-5 h-5 mr-2" />
                        Ajouté
                      </>
                    ) : (
                      <>
                        <ShoppingBag className="w-5 h-5 mr-2" />
                        Ajouter au panier
                      </>
                    )}
                  </Button>
                </div>

                {/* Stock indicator */}
                {product.stock > 0 ? (
                  <p className="text-sm text-green-600 mb-6">
                    {product.stock} en stock
                  </p>
                ) : (
                  <p className="text-sm text-destructive mb-6">
                    Rupture de stock
                  </p>
                )}

                {/* Details accordion */}
                <Accordion type="single" collapsible className="border-t border-border">
                  {product.dimensions && (
                    <AccordionItem value="dimensions">
                      <AccordionTrigger className="font-heading" data-testid="accordion-dimensions">
                        Dimensions
                      </AccordionTrigger>
                      <AccordionContent>
                        {product.dimensions}
                      </AccordionContent>
                    </AccordionItem>
                  )}
                  {product.material && (
                    <AccordionItem value="material">
                      <AccordionTrigger className="font-heading" data-testid="accordion-material">
                        Matériaux
                      </AccordionTrigger>
                      <AccordionContent>
                        {product.material}
                      </AccordionContent>
                    </AccordionItem>
                  )}
                  <AccordionItem value="delivery">
                    <AccordionTrigger className="font-heading" data-testid="accordion-delivery">
                      Livraison
                    </AccordionTrigger>
                    <AccordionContent>
                      Livraison en France métropolitaine sous 5 à 10 jours ouvrés. 
                      Retrait gratuit en boutique au 26 rue Marceau, 37000 Tours.
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </motion.div>
            </div>
          </div>

          {/* Related products */}
          {relatedProducts.length > 0 && (
            <section className="mt-20 pt-12 border-t border-border">
              <h2 className="font-heading text-2xl font-semibold mb-8">
                Complétez votre intérieur
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                {relatedProducts.map((item) => (
                  <Link
                    key={item.id}
                    to={`/produit/${item.slug}`}
                    className="group product-card"
                    data-testid={`related-${item.slug}`}
                  >
                    <div className="aspect-[3/4] overflow-hidden rounded-md mb-3 bg-muted">
                      <img
                        src={optimizeImg(item.images?.[0], 400) || 'https://images.unsplash.com/photo-1726208224138-80303abb73d3?crop=entropy&cs=srgb&fm=jpg&q=85'}
                        alt={item.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="font-medium text-sm group-hover:text-primary transition-colors">
                      {item.name}
                    </h3>
                    <p className="text-primary font-semibold">
                      {typeof item.price === 'number' ? item.price.toFixed(2) : '—'} €
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
